import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDriverLoginSession } from '@/hooks/useDriverLoginSession';
import { monitoring } from '@/lib/monitoring';
import { Truck, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface DriverProtectedRouteProps {
  children: React.ReactNode;
}

export const DriverProtectedRoute = ({ children }: DriverProtectedRouteProps) => {
  const {
    user,
    company,
    loading: authLoading,
    authError,
    refreshProfile,
    refreshOrgClaims,
  } = useAuth();
  const { isValid, isLoading: sessionLoading } = useDriverLoginSession();
  const location = useLocation();
  const driverProfileWarnRef = useRef<string | null>(null);
  const [profileRetryBusy, setProfileRetryBusy] = useState(false);

  const isDriverSessionConfirmed = !!user && isValid && !!company;
  const profileLoadStuck =
    !!user?.id &&
    isValid &&
    !company &&
    !authLoading &&
    !sessionLoading;

  useEffect(() => {
    const stuck =
      !!user?.id &&
      isValid &&
      !company &&
      !authLoading &&
      !sessionLoading;
    if (!stuck) {
      driverProfileWarnRef.current = null;
      return;
    }
    const uid = user.id;
    if (driverProfileWarnRef.current === uid) return;
    monitoring.captureWarning('driver_company_null_on_ready', {
      userId: uid,
      isValid,
      hasAuthError: !!authError,
    });
    driverProfileWarnRef.current = uid;
  }, [user, user?.id, isValid, company, authLoading, sessionLoading, authError]);

  const handleProfileRetry = async () => {
    setProfileRetryBusy(true);
    try {
      await refreshOrgClaims();
      await refreshProfile();
    } finally {
      setProfileRetryBusy(false);
    }
  };

  if (profileLoadStuck) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4">
        <Alert className="max-w-md border-orange-500/40 bg-slate-950/90 text-blue-100">
          <AlertDescription className="space-y-4 text-sm leading-relaxed">
            <p>
              {authError?.trim()
                ? authError
                : 'Não foi possível carregar seu perfil. Verifique sua conexão e tente novamente.'}
            </p>
            <Button
              variant="outline"
              className="w-full bg-orange-500/20 border-orange-500/50 text-orange-200 hover:bg-orange-500/30"
              disabled={profileRetryBusy}
              onClick={handleProfileRetry}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${profileRetryBusy ? 'animate-spin' : ''}`} />
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!isDriverSessionConfirmed && (authLoading || sessionLoading)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="bg-orange-500/20 p-4 rounded-xl">
            <Truck className="h-12 w-12 text-orange-500 animate-pulse" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
          </div>
          <p className="text-blue-200/70 text-sm">Verificando sessão...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/driver" state={{ from: location }} replace />;
  }

  if (!isValid) {
    return <Navigate to="/driver" state={{ from: location, needsDriverSession: true }} replace />;
  }

  return <>{children}</>;
};
